import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Grid, Select, Typography, Hidden, Button, withTheme } from '@material-ui/core';
import debounce from 'lodash.debounce';
import LazyLoad from 'react-lazyload';

// CONSTANTS
import { LOCATIONS_CHANNELS_PAGE } from '@/constants/locations';
import { ContentType } from '@/constants/watchList';

// UTILITY
import store from '@/utility/store';
import { trimCharacters } from '@/utility/common';

// STORE
import { ChannelsActions, CountiesActions, NotifActions } from '@/store/actions';
import { customAnalyticsEvent } from '@/utility/analytics';

// COMPONENTS
import CheckBoxCard from '@/components/Form/CheckBoxCard';
import ChannelsListSkeleton from '@/components/Skeletons/ChannelsListSkeleton';
import SearchChannels from '@/components/AutoComplete/SearchChannels';

// HELP
import ZipCode from './ZipCode';

// STYLES
import {
  ChannelStepContainer,
  ChannelHeader,
  TitleContainer,
  Title,
  TopChannelsContainer,
  ChannelsContainer,
  ChannelsWrapper,
  GenreMenuItem,
  StyledSticky,
} from '../styles';

class ChannelsStep extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showCountyStep: false,
      search: '',
      autocompleteValue: null,
      showChannels: true,
      filters: props.filters,
    };

    this.debouncedGetChannels = debounce((data, cb = () => {}) => {
      props.getChannels(data);
      cb();
    }, 200);

    this.debouncedChannelsAutocomplete = debounce((data, cb = () => {}) => {
      props.channelsAutocomplete(data);
      cb();
    }, 500);
    this.filters = React.createRef();
    this.inputAutocomplete = React.createRef();
  }

  componentDidMount() {
    const { partner, filters } = this.props;

    if (filters.length) {
      this.upgradeFilters();
    }

    this.setState({
      showChannels: partner.slug !== 'wow-temp-turnoff',
    });
  }

  componentDidUpdate(prevProps) {
    const {
      values,
      counties,
      startData,
      setFieldValue,
      getChannels,
      getTopChannels,
      onChangeZipCode,
      partner,
      filters,
    } = this.props;

    const isPartnerUpdated = prevProps.partner !== partner && partner;
    const isFiltersUpdated = prevProps.filters !== filters && filters;

    if (isPartnerUpdated) {
      this.setState({
        showChannels: partner.slug !== 'wow-temp-turnoff',
      });
    }

    if (isFiltersUpdated || isPartnerUpdated) {
      this.upgradeFilters();
    }

    if (prevProps.counties !== counties && counties && !startData.zipcode) {
      let dma_id = '';
      let county_id_fk = '';
      let zipcode = '';
      const sports = startData.sports || '';
      const live_local_news =
        startData?.live_local_news === 'yes' ? { live_local_news: 'yes' } : {};

      if (counties.length > 1) {
        this.setState({ showCountyStep: true });
      } else if (counties.length === 1) {
        zipcode = values.zipcode;
        dma_id = counties[0].dma_id_fk;
        county_id_fk = counties[0].county_id;

        const submitData = {
          dma_id_fk: dma_id,
          search: values.search,
          filters: values.genre_search,
        };

        setFieldValue('dma_id', dma_id);
        setFieldValue('county_id_fk', county_id_fk);
        getChannels(submitData);
        getTopChannels({ dma_id_fk: dma_id, sports, ...live_local_news });
        onChangeZipCode(zipcode);
      } else {
        zipcode = values.zipcode;
        onChangeZipCode(zipcode);
      }

      store.set('start-data', { ...startData, zipcode, dma_id, county_id_fk });
    }
  }

  componentWillUnmount() {
    const { filters, partner, setSelectedFilter } = this.props;

    if (partner.slug === 'wow-temp-turnoff') {
      setSelectedFilter(filters[0]);
    }
  }

  upgradeFilters = () => {
    const { t, filters, partner, setSelectedFilter } = this.props;
    if (partner.slug === 'wow-temp-turnoff') {
      let allItem = null;
      const wowFilters = filters.slice();
      const allItemIndex = wowFilters.findIndex(filter => filter.genre_id === 'c2');

      if (allItemIndex !== -1) {
        allItem = wowFilters.splice(allItemIndex, 1);
      }

      const chooseGenreItem = { genre_id: '', genre_name: t('common.chooseGenre') };
      wowFilters.unshift(chooseGenreItem);

      if (allItem) {
        wowFilters.push(allItem[0]);
      }

      this.setState({ filters: wowFilters }, () => setSelectedFilter(chooseGenreItem));
    } else {
      this.setState({ filters });
    }
  };

  handleSearchChange = (event, searchValue, reason) => {
    if (event.persist) {
      event.persist();
    }

    if (event?.keyCode === 13 && reason === 'reset') {
      return;
    }

    const { search } = this.state;
    const { startData, values } = this.props;
    const dma_id_fk = startData.dma_id || values.dma_id;
    let newInputValue = searchValue;

    if (reason === 'reset' || reason === 'select-option' || reason === 'clear') {
      newInputValue = '';
    }

    if (search !== newInputValue) {
      const submitData = {
        dma_id_fk,
        search: newInputValue,
      };

      this.setState({ search: newInputValue });

      this.debouncedChannelsAutocomplete(submitData);
    }
  };

  handleFilterChange = event => {
    const { showChannels } = this.state;
    const { handleChange, getChannels, startData, values, setFieldValue } = this.props;
    const value = event.target.value;
    const dma_id_fk = startData.dma_id || values.dma_id;

    const submitData = {
      dma_id_fk,
      search: '',
      filters: value,
    };

    if (values.search !== '') {
      setFieldValue('search', '');
    }

    if (!showChannels) {
      this.handleShowChannels();
    }

    handleChange(event);
    getChannels(submitData);
  };

  handleSearchSelect = (event, value = {}, reason, setOpen) => {
    if (event.persist) {
      event.persist();
    }

    const { t, values, onChannelSelect, setFieldValue, setValues, pushNotification } = this.props;

    const analyticsData = {
      location: 'Bottom Search',
      current_search: value.name,
    };

    if (reason === 'select-option') {
      const isChannelsBundle = value.type === 'channel_bundle';

      if (isChannelsBundle) {
        const channelsBundle = value.channels.map(channel => channel.channel_id.toString());
        const channels = [...new Set([...values.channels, ...channelsBundle])];

        setValues({ ...values, channels });

        onChannelSelect(value.channels, analyticsData);

        pushNotification({
          message: t('notification.channelsBundleSuccessfullyAdded'),
          options: {
            variant: 'success',
          },
        });

        setOpen(false);

        return;
      }

      const hasChannel = values.channels.includes(value?.channel_id?.toString());

      const updateSelectedChannels = () => {
        const isContentType = value.type === ContentType.SHOW || value.type === ContentType.MOVIE;
        const selected_content = [...values.selected_content];
        const hasContent = selected_content.find(
          item =>
            item.channel_id === value.pivot?.channel_id &&
            item.streaming_content_id === value.pivot?.streaming_content_id,
        );

        if (isContentType && !hasContent) {
          selected_content.push(value.pivot);
        }

        return selected_content;
      };

      const selected_content = updateSelectedChannels();

      if (!hasChannel) {
        const channels = [...values.channels, value?.channel_id?.toString()];

        setValues({ ...values, channels, selected_content });
        onChannelSelect(value, analyticsData);

        pushNotification({
          message: t('notification.channelsSuccessfullyAdded', { channelName: value.name }),
          options: {
            variant: 'success',
          },
        });
      } else {
        setFieldValue('selected_content', selected_content);

        pushNotification({
          message: t('notification.channelAlreadySelected', { channelName: value.name }),
          options: {
            variant: 'success',
          },
        });
      }

      setOpen(false);
    }
  };

  handleSearchClear = () => {
    const { search } = this.state;
    const { channelsSetAutocompleteDefData } = this.props;

    if (search) {
      this.setState({ search: '' }, () => channelsSetAutocompleteDefData());
    }
  };

  handleSelectChannel = (event, data, location) => {
    const { search } = this.state;
    const { handleChange, onChannelSelect } = this.props;

    const analyticsData = {
      location,
      current_search: search,
    };

    handleChange(event);
    onChannelSelect(data, analyticsData);
  };

  handleSubmitZipCode = () => {
    const { getCounties, values, startData } = this.props;

    getCounties(
      values.zipcode,
      startData.partner_id_fk,
      startData.unique_id,
      LOCATIONS_CHANNELS_PAGE,
    );
  };

  scrollToFilters = () => {
    window?.scrollTo({ top: this.filters.current.offsetTop, left: 0, behavior: 'smooth' });
  };

  handleSearchClose = () => {
    this.setState({ search: '' });
  };

  handleShowChannels = () => {
    const { showChannels } = this.state;
    const { partner, startData } = this.props;

    if (partner.slug === 'wow-temp-turnoff') {
      const unique_id = startData.unique_id;

      customAnalyticsEvent('Clicked View More Channels', { unique_id });
    }

    this.setState({ showChannels: !showChannels });
  };

  handleAutocompleteFocus = () => {
    const { search } = this.state;
    const { startData, channelsAutocomplete, values, autocompleteData } = this.props;

    if (!search && !autocompleteData.length) {
      const dma_id_fk = startData.dma_id || values.dma_id;

      channelsAutocomplete({ dma_id_fk });
    }
    this.scrollToFilters();
  };

  handleZipCodeChange = event => {
    const { setFieldValue } = this.props;

    setFieldValue('zipcode', trimCharacters(event.target.value));
  };

  handleCountyChange = event => {
    const {
      startData,
      getChannels,
      getTopChannels,
      values,
      setFieldValue,
      handleChange,
      onChangeZipCode,
    } = this.props;

    const county_id_fk = event.target.value;
    const dma_id = event.target?.dataset?.dma_id_fk || '';
    const sports = startData.sports || '';
    const live_local_news = startData?.live_local_news === 'yes' ? { live_local_news: 'yes' } : {};
    const submitData = {
      dma_id_fk: dma_id,
      county_id_fk,
      search: values.search,
      filters: values.genre_search,
    };

    setFieldValue('dma_id', dma_id);
    handleChange(event);

    store.set('start-data', { ...startData, zipcode: values.zipcode, dma_id });

    getChannels(submitData);
    getTopChannels({ dma_id_fk: dma_id, sports, ...live_local_news });
    onChangeZipCode(values.zipcode);
  };

  render() {
    const { showCountyStep, search, autocompleteValue, showChannels, filters } = this.state;
    const {
      t,
      values,
      errors,
      touched,
      handleBlur,
      loading,
      loadingChannelsTop,
      channels,
      channelsTop,
      channelsLiveLocalNews,
      counties,
      startData,
      autocompleteData,
      autocompleteDataLoading,
      isIframe,
      partner,
      navButtons,
      theme,
    } = this.props;

    const textOverwrites = partner?.text_overwrites || {};
    let popoverWrapper = 'popover-wrapper ';
    const dropdownContainer = 'popover-wrapper__dropdown-container';
    const showZipCode = !startData.zipcode || (!startData.zipcode && !startData.skipZip);

    if (partner?.slug) {
      popoverWrapper += ` partner-${partner.slug}`;
    }

    const GenreMenuProps = {
      className: popoverWrapper,
      PaperProps: {
        className: dropdownContainer,
      },
    };

    const getChannelsContent = () => {
      return (
        <ChannelsWrapper>
          <ChannelsContainer className="channels-container" container spacing={1}>
            {loading ? (
              <ChannelsListSkeleton />
            ) : (
              <>
                {channels.map(channel => (
                  <Grid item xs={3} sm={2} md={2} key={channel.channel_id}>
                    <CheckBoxCard
                      name="channels"
                      value={channel.channel_id.toString()}
                      imageSrc={channel.img}
                      title={channel.name}
                      handleChange={event => {
                        this.handleSelectChannel(event, channel, 'Bottom Search');
                      }}
                      handleBlur={handleBlur}
                      checkedValue={values.channels}
                      withLazyLoad
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography className="m-b-15 m-t-15" align="center" variant="h5">
                    {t('channels.channels.trySearchingSearchBox')}
                  </Typography>
                </Grid>
              </>
            )}
            {!loading && !channels.length && values.search ? (
              <Grid item xs={12}>
                <Typography className="m-b-15" align="center" variant="subtitle2">
                  {t('common.noSearchResults')}
                </Typography>
              </Grid>
            ) : null}
          </ChannelsContainer>
        </ChannelsWrapper>
      );
    };

    return (
      <ChannelStepContainer className="step">
        <div className="page-content">
          <Grid container justifyContent="center">
            <Grid item md={8}>
              <TitleContainer>
                <Title variant="h4" align="center" component="h1">
                  {textOverwrites.fmb_channels_title || t('channels.channels.title')}
                </Title>
                <Typography variant="subtitle2" align="center">
                  {textOverwrites.fmb_channels_subtitle || t('channels.channels.subtitle')}
                </Typography>
              </TitleContainer>
              {showZipCode && (
                <ZipCode
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  counties={counties}
                  showCountyStep={showCountyStep}
                  onSubmitPress={this.handleSubmitZipCode}
                  onChangeZipCode={this.handleZipCodeChange}
                  onChangeCounty={this.handleCountyChange}
                />
              )}
              {/* <Grid container spacing={2} className="m-b-30">
                    <Grid item md={12} className="m-b-10">
                      <Skeleton style={{ height: '20px', transform: 'none' }} />
                    </Grid>
                    <Grid item md={9}>
                      <Skeleton style={{ height: '60px', transform: 'none' }} />
                    </Grid>
                    <Grid item md={3}>
                      <Button fullWidth style={{ height: '50px', margin: '5px' }} disabled></Button>
                    </Grid>
                  </Grid> */}
            </Grid>
          </Grid>
          {!!channelsLiveLocalNews.length && (
            <>
              <Typography className="m-b-15" align="center" variant="h6">
                {t('channels.localNewsSection.title')}
              </Typography>
              <TopChannelsContainer container spacing={1}>
                {loadingChannelsTop ? (
                  <ChannelsListSkeleton />
                ) : (
                  <>
                    {channelsLiveLocalNews.map((channel, index) => {
                      if (index > 23) {
                        return;
                      }

                      return (
                        <Grid item xs={3} sm={2} md={2} key={channel.channel_id}>
                          <CheckBoxCard
                            name="channels"
                            value={channel.channel_id.toString()}
                            imageSrc={channel.img}
                            title={channel.name}
                            handleChange={event =>
                              this.handleSelectChannel(event, channel, 'Live Local News')
                            }
                            handleBlur={handleBlur}
                            checkedValue={values.channels}
                          />
                        </Grid>
                      );
                    })}
                  </>
                )}
              </TopChannelsContainer>
            </>
          )}
          <Typography className="m-b-15" align="center" variant="h6">
            {textOverwrites.fmb_popular_channels || t('channels.popularSection.title')}
          </Typography>
          <TopChannelsContainer className="pop-channels-wdio" container spacing={1}>
            {loadingChannelsTop ? (
              <ChannelsListSkeleton />
            ) : (
              <>
                {channelsTop.map((channel, index) => {
                  if (index > 23) {
                    return;
                  }

                  return (
                    <Grid item xs={3} sm={2} md={2} key={channel.channel_id}>
                      <CheckBoxCard
                        name="channels"
                        value={channel.channel_id.toString()}
                        imageSrc={channel.img}
                        title={channel.name}
                        handleChange={event => this.handleSelectChannel(event, channel, 'Top 25')}
                        handleBlur={handleBlur}
                        checkedValue={values.channels}
                      />
                    </Grid>
                  );
                })}
              </>
            )}
          </TopChannelsContainer>
        </div>
        <div ref={this.filters} />
        <StyledSticky enabled={!isIframe} innerZ={10}>
          <ChannelHeader className="channel-header" theme={theme}>
            <div className="page-content">
              <Grid container justifyContent="center">
                <Grid item xs={12} sm={10} md={8} lg={7}>
                  <Grid className="channel-header__fields-container" container spacing={4}>
                    <Grid item xs={12} sm={6}>
                      <Hidden xsDown implementation="js">
                        <Typography className="m-b-15" align="center" variant="h6">
                          {t('channels.filters.searchLabel')}
                        </Typography>
                      </Hidden>
                      <SearchChannels
                        className="search-channels"
                        freeSolo
                        innerRef={this.inputAutocomplete}
                        inputValue={search}
                        value={autocompleteValue}
                        onInputChange={this.handleSearchChange}
                        onChange={this.handleSearchSelect}
                        options={autocompleteData}
                        loading={autocompleteDataLoading}
                        openOnFocus
                        inputProps={{
                          onFocus: this.handleAutocompleteFocus,
                          onBlur: this.handleSearchClear,
                        }}
                        onClose={this.handleSearchClose}
                        analyticsLocation={LOCATIONS_CHANNELS_PAGE}
                      />
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      className="channel-header__fields-container-item-genre"
                    >
                      <Hidden xsDown implementation="js">
                        <Typography className="m-b-15" align="center" variant="h6">
                          {t('channels.filters.genreLabel')}
                        </Typography>
                      </Hidden>
                      <Select
                        name="genre_search"
                        disabled={!filters.length}
                        value={values.genre_search}
                        onChange={this.handleFilterChange}
                        onBlur={handleBlur}
                        error={touched.genre_search && !!errors.genre_search}
                        className="genre-select"
                        MenuProps={GenreMenuProps}
                        displayEmpty
                      >
                        {filters.map(filter => (
                          <GenreMenuItem
                            disabled={!filter.genre_id}
                            value={filter.genre_id.toString()}
                            key={`${filter.genre_id}`}
                            className="genre_select_item"
                          >
                            {filter.genre_id === 'c2' ? (
                              <>
                                <Hidden xsDown>{filter.genre_name}</Hidden>
                                <Hidden smUp>{t('channels.filters.filterByGenres')}</Hidden>
                              </>
                            ) : (
                              filter.genre_name
                            )}
                          </GenreMenuItem>
                        ))}
                      </Select>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </ChannelHeader>
        </StyledSticky>
        <div className="page-content second-channel-selection">
          {showChannels ? (
            <>
              {partner.slug === 'wow-temp-turnoff' && navButtons}
              {isIframe ? getChannelsContent() : <LazyLoad once>{getChannelsContent()}</LazyLoad>}
            </>
          ) : (
            <Grid container alignItems="center" direction="column" spacing={4} className="m-b-15">
              <Grid item>
                <Typography className="m-t-40 looking-for-more" align="center" variant="h5">
                  {t('channels.channels.lookingForMore')}
                </Typography>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.handleShowChannels}
                  className="viewMoreChannels"
                >
                  {t('buttons.viewMoreChannels')}
                </Button>
              </Grid>
            </Grid>
          )}
        </div>
      </ChannelStepContainer>
    );
  }
}

ChannelsStep.defaultProps = {
  onChannelSelect: () => {},
  startData: {},
};

ChannelsStep.propTypes = {
  t: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  loadingChannelsTop: PropTypes.bool.isRequired,
  formDataLoading: PropTypes.bool.isRequired,
  channels: PropTypes.array.isRequired,
  channelsTop: PropTypes.array.isRequired,
  channelsLiveLocalNews: PropTypes.array.isRequired,
  getChannels: PropTypes.func.isRequired,
  getTopChannels: PropTypes.func.isRequired,
  onChannelSelect: PropTypes.func,
  counties: PropTypes.oneOfType([PropTypes.array, PropTypes.oneOf([null])]),
  getCounties: PropTypes.func.isRequired,
  countiesLoading: PropTypes.bool.isRequired,
  startData: PropTypes.object,
  onChangeZipCode: PropTypes.func.isRequired,
  channelsAutocomplete: PropTypes.func.isRequired,
  autocompleteDataLoading: PropTypes.bool.isRequired,
  autocompleteData: PropTypes.array.isRequired,
  channelsSetAutocompleteDefData: PropTypes.func.isRequired,
  pushNotification: PropTypes.func.isRequired,
  isIframe: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  setSelectedFilter: PropTypes.func.isRequired,
  navButtons: PropTypes.element.isRequired,
};

const mapStateToProps = ({ app, channels, counties }) => ({
  isIframe: app.isIframe,
  channels: channels.channels,
  channelsTop: channels.channelsTop,
  channelsLiveLocalNews: channels.channelsLocalNews,
  filters: channels.filters,
  loading: channels.loadings.channels,
  loadingChannelsTop: channels.loadings.channelsTop,
  formDataLoading: channels.loadings.formData,
  autocompleteDataLoading: channels.loadings.autocompleteData,
  autocompleteData: channels.autocompleteData,
  counties: counties.counties,
  countiesLoading: counties.loading,
});

const mapDispatchToProps = dispatch => {
  return {
    getChannels: data => dispatch(ChannelsActions.getChannels.action({ data })),
    getTopChannels: data => dispatch(ChannelsActions.getTopChannels.action({ data })),
    getCounties: (zipcode, partner_id_fk, unique_id, location) => {
      dispatch(CountiesActions.getCounties.action({ zipcode, partner_id_fk, unique_id, location }));
    },
    channelsAutocomplete: data => dispatch(ChannelsActions.channelsAutocomplete.action({ data })),
    channelsSetAutocompleteDefData: () => {
      dispatch(ChannelsActions.channelsSetAutocompleteDefData.action());
    },
    pushNotification: params => dispatch(NotifActions.pushNotification.action(params)),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withTheme,
)(ChannelsStep);
